import React, { useCallback, useMemo } from 'react';

import { useCustomer } from '@order/Customer';
import { getUrqlError } from '@order/utils';

import type {
  SubmitBraintreeBillingAccountSuccessResponse,
  UserError,
} from '../../../graphql/types';
import type { CreditCardForm as Form } from '../CreditCardForm.types';
import type { SubmitBraintreeBillingAccountMutation } from '../GraphQL/CreditCardForm.generated';
import {
  useDeleteBillingAccountMutation,
  useSubmitBraintreeBillingAccountMutation,
  useUpdateBillingAccountMutation,
} from '../GraphQL/CreditCardForm.generated';
import { useCreditCardErrorHandling } from './CreditCardForm.hooks.errorHandling';

export const useCreditCardHandlers = ({
  paymentMethodId,
  onComplete,
}: CreditCardHandlersProps = {}) => {
  const notifyError = useCreditCardErrorHandling();

  // ────────────────────────────── State ───────────────────────────────────────
  const { customer } = useCustomer();
  const {
    submitBillingAccountResponse,
    submitBillingAccount,
    parseBillingAccountResponse,
  } = useSubmitBillingAccount();
  const [updateBillingAccountResponse, updateBillingAccount] =
    useUpdateBillingAccountMutation();
  const [deleteBillingAccountResponse, deleteBillingAccount] =
    useDeleteBillingAccountMutation();

  // ───────────────────────────- Callbacks ────────────────────────────────────

  const handleOnSave = useCallback(
    (form: Form, nonce: string) => {
      (async () => {
        if (!nonce) return;
        const response = await submitBillingAccount({
          input: {
            nonce,
            nickname: form.nickname,
            isDefault: form.default,
          },
        });

        // Feature flagged billing account submission response.
        const { typename, billingAccount, errorMessage, isKnownUserError } =
          parseBillingAccountResponse(response.data);

        // Successfully added billing account.
        if (billingAccount?.id) {
          onComplete?.();

          return;
        }

        // Handling known user errors.
        if (isKnownUserError) {
          notifyError({
            typename,
            errorMessage,
            systemError: errorMessage,
          });

          return;
        }

        // Handling unknown user error.
        if (errorMessage) {
          notifyError({ errorMessage, systemError: typename });

          return;
        }

        // Handling system error.
        if (response?.error) {
          notifyError({
            systemError: getUrqlError(response?.error),
          });
        }
      })();
    },
    [
      submitBillingAccount,
      parseBillingAccountResponse,
      onComplete,
      notifyError,
    ],
  );

  const handleOnEdit = useCallback(
    (form: Form) => {
      (async () => {
        if (!paymentMethodId) return;
        const response = await updateBillingAccount({
          input: {
            id: Number(paymentMethodId),
            nickname: form.nickname,
            isDefault: form.default,
          },
        });
        const billingAccount =
          response.data?.updateBillingAccount?.billingAccount;

        // Successfully edited billing account.
        if (billingAccount?.id) {
          onComplete?.();
        }

        // Handling system error.
        if (response?.error) {
          notifyError({
            systemError: getUrqlError(response?.error),
          });
        }
      })();
    },
    [paymentMethodId, updateBillingAccount, onComplete, notifyError],
  );

  const handleOnDelete = useCallback(() => {
    (async () => {
      if (!paymentMethodId) return;
      const response = await deleteBillingAccount({
        input: {
          id: Number(paymentMethodId),
        },
      });

      // Handling system error.
      if (response?.error) {
        notifyError({
          isDeleteRequest: true,
          systemError: getUrqlError(response?.error),
        });
      }
    })();
  }, [deleteBillingAccount, notifyError, paymentMethodId]);

  return React.useMemo(
    () => ({
      customer,
      submitBillingAccountResponse,
      updateBillingAccountResponse,
      deleteBillingAccountResponse,
      handleOnSave,
      handleOnEdit,
      handleOnDelete,
      parseBillingAccountResponse,
    }),
    [
      customer,
      submitBillingAccountResponse,
      updateBillingAccountResponse,
      deleteBillingAccountResponse,
      handleOnSave,
      handleOnEdit,
      handleOnDelete,
      parseBillingAccountResponse,
    ],
  );
};

export const useSubmitBillingAccount = () => {
  const [submitBillingAccountResponse, submitBillingAccount] =
    useSubmitBraintreeBillingAccountMutation();

  // Parsing mutation response.
  const parseBillingAccountResponse = useCallback(
    (response?: SubmitBraintreeBillingAccountMutation) => {
      // Data and typename.
      const data = response?.submitBraintreeBillingAccount;
      const typename = data?.__typename ?? '';

      // Billing account.
      const billingAccount = (
        data as SubmitBraintreeBillingAccountSuccessResponse
      )?.billingAccount;

      // Error message.
      const errorMessage = (data as unknown as UserError)?.errorMessage;

      // User errors we currently expect.
      const isKnownUserError = [
        'InvalidSubmittedBillingAccount',
        'InvalidPostalCode',
        'TooManyRequests',
      ].includes(typename);

      return {
        typename,
        billingAccount,
        errorMessage,
        isKnownUserError,
      };
    },
    [],
  );

  return useMemo(
    () => ({
      submitBillingAccountResponse,
      submitBillingAccount,
      parseBillingAccountResponse,
    }),
    [
      submitBillingAccountResponse,
      submitBillingAccount,
      parseBillingAccountResponse,
    ],
  );
};

type CreditCardHandlersProps = {
  readonly paymentMethodId?: string;
  readonly onComplete?: () => void;
};
